/**
 * Safe localStorage wrapper that never throws.
 *
 * In Safari private/incognito mode, quota-exceeded situations, or when
 * cookies are blocked, `localStorage.setItem` throws `QuotaExceededError`
 * and `localStorage.getItem` can throw in rare edge cases. This module
 * catches those errors and falls back to an in-memory `Map` so the app
 * continues to function — persistence is lost on reload, but the page
 * never crashes.
 */

const memoryStore = new Map<string, string>();

let storageAvailable = false;

if (typeof window !== 'undefined') {
  try {
    const testKey = '__cdf_storage_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    storageAvailable = true;
  } catch {
    storageAvailable = false;
  }
}

/** Re-check availability (e.g. after the user exits private mode in a new tab). */
function checkStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const testKey = '__cdf_storage_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

export function safeGetItem(key: string): string | null {
  if (storageAvailable) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      // Fall through to memory store
    }
  }
  return memoryStore.get(key) ?? null;
}

export function safeSetItem(key: string, value: string): void {
  if (storageAvailable) {
    try {
      window.localStorage.setItem(key, value);
      return;
    } catch {
      // Quota exceeded or private mode — fall back to memory.
      // Mark as unavailable so we skip the try/catch overhead on subsequent calls.
      storageAvailable = false;
    }
  }
  memoryStore.set(key, value);
}

export function safeRemoveItem(key: string): void {
  if (storageAvailable) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // no-op
    }
  }
  memoryStore.delete(key);
}

/** Number of items in storage (localStorage or memory fallback). */
export function safeStorageLength(): number {
  if (storageAvailable) {
    try {
      return window.localStorage.length;
    } catch {
      // Fall through
    }
  }
  return memoryStore.size;
}

/** Key at the given index, or null if out of range. */
export function safeStorageKey(index: number): string | null {
  if (storageAvailable) {
    try {
      return window.localStorage.key(index);
    } catch {
      // Fall through
    }
  }
  return Array.from(memoryStore.keys())[index] ?? null;
}

/** True when localStorage is available and writable. */
export function isStoragePersistent(): boolean {
  return storageAvailable || checkStorage();
}
